/**
 * scopeRescue.ts — put Blake's body-appended overlays back inside the CSS scope.
 *
 * 🔴 THE DEFECT, AND IT IS OURS NOT HIS. His bundle appends three overlays
 * straight to `document.body`:
 *
 *   app.js:3494  #gacha-back        the daily-prize gachapon
 *   app.js:5441  #ex-review-back    the Exchange REVIEW OFFER modal
 *   app.js:5452  #ex-success-host   the Exchange post-send success screen
 *
 * Standalone that is fine — his page loads `styles.css` unscoped. Our host does
 * not: it loads `styles.scoped.css`, every rule of which lives inside
 * `@scope (.blake-install-scope)`, and it mounts his markup in
 * `<div class="blake-install-scope"><div id="app">` inside React's `#root`
 * (WebInstall.tsx). A node appended to `body` is a SIBLING of `#root` — an
 * ancestor-side node, not a descendant of the scope root — and `@scope` matches
 * only the root and its descendants. So not one rule applies to it.
 *
 * What that looks like to a person: `.modal-back` carries `position: fixed`,
 * `inset: 0`, the dim background, and `display: none` flipped to `grid` by
 * `.open`. With NONE of it applied, the overlay does not overlay — it renders in
 * normal flow as unstyled text at the very bottom of the document, below the
 * footer. The Exchange route scrolls to top and the button sits mid-page, so
 * nobody ever scrolls far enough to see it.
 *
 * 🪤 IT READS EXACTLY LIKE A DEAD BUTTON, and that is how it was reported: the
 * builder populates correctly, REVIEW OFFER does nothing. The listener fires,
 * the markup inserts, the modal is simply invisible. Two hypotheses were checked
 * and killed before this was found — the write guard (`#ex-review` is not in
 * GUARDED_WRITES) and the `canReview` gate (its three clauses were all satisfied).
 *
 * ⇒ Move them, rather than restyling them. Duplicating `.modal-back` into
 * `overrides.css` would fork a rule that already exists and let the two drift;
 * re-parenting is one operation and the scoped sheet then applies unchanged.
 *
 * 🔑 SAFE TO RE-PARENT. His own teardowns are id lookups and `.remove()`
 * (`$("#ex-review-back")`, `$("#gacha-back").remove()`), none of which care where
 * the node lives, and `remove()` works from any parent. Checked, not assumed.
 */

/** The three roots, enumerated from his three `document.body` inserts.
 *  🔑 ENUMERATED, NOT SAMPLED — and pinned: scopeRescue.test.ts greps app.js for
 *  `document.body.insertAdjacentHTML` and fails if a fourth appears without an
 *  entry here, the same mechanical-derivation posture writeGuard uses. */
export const RESCUED_OVERLAY_IDS: readonly string[] = [
  "ex-review-back",
  "ex-success-host",
  "gacha-back",
  /* The subcategory flyout (2026-09-05, PR #746 review): position: fixed
     under the category strip, appended to body so no ancestor's overflow
     clips it, and therefore outside the scope root until rescued. */
  "subnav-panel",
];

/**
 * Watches `document.body` for those overlays and re-parents each into the scope
 * root as it appears.
 *
 * childList WITHOUT subtree: all three are inserted as direct children of body,
 * and a subtree observer would wake on every mutation React makes anywhere in
 * the document for no gain.
 *
 * Returns a teardown that disconnects, which the tests need because they share
 * one jsdom window across cases.
 */
export function installScopeRescue(win: Window, scopeRoot: Element | null): () => void {
  if (!scopeRoot) return () => {};

  const rescue = (node: Node): void => {
    if (node.nodeType !== 1) return;
    const el = node as Element;
    if (!RESCUED_OVERLAY_IDS.includes(el.id)) return;
    if (scopeRoot.contains(el)) return;
    /* Moving fires a childList record on BODY for the removal, and one on the
       scope root for the insertion. Neither re-enters this: removals are not
       read, and the root is not observed. */
    scopeRoot.appendChild(el);
  };

  // Anything already appended before this installed — the observer only sees
  // future mutations, and the gachapon can fire early.
  for (const id of RESCUED_OVERLAY_IDS) {
    const existing = win.document.getElementById(id);
    if (existing && existing.parentElement === win.document.body) rescue(existing);
  }

  /* The GLOBAL MutationObserver, not win.MutationObserver — the same call
     followUser makes, and the only one the Window type declares. */
  const observer = new MutationObserver((records: MutationRecord[]) => {
    for (const record of records) {
      for (const node of Array.from(record.addedNodes)) rescue(node);
    }
  });
  observer.observe(win.document.body, { childList: true });
  return () => observer.disconnect();
}
